import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Icon from 'components/common/Icon';
import { createBillingPortalSession } from 'service/payment';

import styles from './CancelToBillingPortalDialog.module.scss';

export type CancelType = 'prime' | 'prime_plus' | 'membership' | 'izanavi';

export type Props = {
  open: boolean;
  onClose: () => void;
  type: CancelType;
};

const TITLE_BY_TYPE: Record<CancelType, string> = {
  prime: 'プライム登録の解除',
  prime_plus: 'プライムPlusの解除',
  membership: 'メンバーシップの解除',
  izanavi: 'IZANAVIの解除',
};

// 親→子 cascade があるのはプライム解除とメンバーシップ解除のみ
const CASCADE_NOTICE_BY_TYPE: Record<CancelType, string | null> = {
  prime:
    '「プライムPlus」を契約中の場合、先にプライム登録だけを解除することはできません。',
  prime_plus: null,
  membership:
    '「IZANAVI」を契約中の場合、先にメンバーシップだけを解除することはできません。',
  izanavi: null,
};

const CASCADE_NOTE_BY_TYPE: Record<CancelType, string | null> = {
  prime:
    'プライム登録を解除すると、紐づく「プライムPlus」も自動的に解除となります。',
  prime_plus: null,
  membership:
    'メンバーシップを解除すると、紐づく「IZANAVI」も自動的に解除となります。',
  izanavi: null,
};

export default function CancelToBillingPortalDialog({
  open,
  onClose,
  type,
}: Props): JSX.Element {
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<'no_customer' | 'unknown' | null>(null);

  const handleClose = () => {
    if (isOpening) return;
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (isOpening) return;
    setError(null);
    setIsOpening(true);
    try {
      const { url } = await createBillingPortalSession();
      window.location.href = url;
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response
        ?.status;
      setError(status === 400 || status === 404 ? 'no_customer' : 'unknown');
      setIsOpening(false);
    }
  };

  const title = TITLE_BY_TYPE[type];
  const cascadeNotice = CASCADE_NOTICE_BY_TYPE[type];
  const cascadeNote = CASCADE_NOTE_BY_TYPE[type];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <div className={styles.dialogWrapper}>
        <div className={styles.closeBtn}>
          <Icon name="close" width={24} height={24} onClick={handleClose} />
        </div>
        <div className={styles.header}>{title}</div>
        <div className={styles.content}>
          <div className={styles.noticeTitle}>注意事項</div>
          <ul className={styles.noticeList}>
            <li>
              <span className={styles.bold}>金額の確認:</span>
              解除するサブスクリプションの金額にお間違いがないかご確認ください。
            </li>
            {cascadeNotice && (
              <li>
                <span className={styles.bold}>解除の順番:</span>
                {cascadeNotice}
              </li>
            )}
          </ul>
          {cascadeNote && <p className={styles.note}>※{cascadeNote}</p>}
          <p className={styles.note}>
            ※遷移先では、Webでご契約中のすべての登録内容が確認出来るため、相違のないようご注意ください。
          </p>
          {error === 'no_customer' && (
            <p className={styles.errorText}>
              決済情報がまだ登録されていないため利用できません。
            </p>
          )}
          {error === 'unknown' && (
            <p className={styles.errorText}>
              サブスク管理画面を開けませんでした。時間をおいて再度お試しください。
            </p>
          )}
        </div>
        <div className={styles.buttons}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleClose}
            disabled={isOpening}
          >
            キャンセル
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={() => void handleConfirm()}
            disabled={isOpening}
          >
            {isOpening ? '読み込み中...' : '解除手続きに進む'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
